import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import omit from 'lodash/omit';

// Multibrand-UI Components
import Text from '@zg-rentals/multibrand-ui/lib/Text';
import Row from '@zg-rentals/multibrand-ui/lib/Row';
import Col from '@zg-rentals/multibrand-ui/lib/Col';
import Link from '@zg-rentals/multibrand-ui/lib/Link';
import Button from '@zg-rentals/multibrand-ui/lib/Button';
import Badge from '@zg-rentals/multibrand-ui/lib/Badge';
import HR from '@zg-rentals/multibrand-ui/lib/HR';

import Close from '@zg-rentals/multibrand-ui/lib/icons/Close';
import Check from '@zg-rentals/multibrand-ui/lib/icons/Check';

import { UserManagementSelectors, UserSelectors } from 'app/shared/selectors';
import UserPermissionsConstants from 'app/shared/constants/UserPermissionsConstants';
import ModalConstants from 'app/shared/constants/ModalConstants';

import UserManagementActions from 'app/shared/actions/UserManagementActions';
import ModalActions from 'app/shared/actions/ModalActions';

import pathsManifest from 'app/shared/pathsManifest';

const paths = pathsManifest.getAllPaths();

const propTypes = {
    onSendInvite: PropTypes.func.isRequired,
    onBack: PropTypes.func.isRequired,
};

const Preview = ({
    onSendInvite,
    onBack,
    onEditRole,
    onEditAllProperties,
    onEditProperties,
    onEditPermissions,
    currentUser,
    dispatch,
    user,
    onCancel,
}) => {
    const isAdmin = currentUser.permissions[UserPermissionsConstants.PERMISSIONS.COMPANY_ADMIN];
    const allProperties = currentUser.permissions[UserPermissionsConstants.PERMISSIONS.SYNC_PROPERTIES] || isAdmin;
    const { isInvitation, isPending } = currentUser;
    const isYou = !isInvitation && !isPending && user.userToken === currentUser.userToken;
    const otherPermissions = omit(UserPermissionsConstants.PERMISSIONS, [
        UserPermissionsConstants.PERMISSIONS.COMPANY_ADMIN,
        UserPermissionsConstants.PERMISSIONS.SYNC_PROPERTIES,
        UserPermissionsConstants.PERMISSIONS.COMPANY_USER,
    ]);

    const handleCancelInvitation = () => {
        dispatch(ModalActions.showModal({
            modalType: ModalConstants.CONFIRMATION_MODAL,
            children: 'Are you sure you want to cancel this invite?',
            submitButtonTitle: 'Confirm',
            header: 'Cancel invite',
            onCancel: () => dispatch(ModalActions.hideModal()),
            onSubmit: () => {
                dispatch(UserManagementActions.cancelInvitation(currentUser.email))
                    .then(() => {
                        window.router.transitionTo(`${paths.userManagementPage}/${paths.userManagementPending}`);
                        dispatch(ModalActions.hideModal());
                    })
                    .catch(() => {
                        // Todo: show error
                    });
            },
            useModalDialog: true,
        }));
    };

    const handleDeactivateAccount = () => {
        dispatch(ModalActions.showModal({
            modalType: ModalConstants.CONFIRMATION_MODAL,
            textContent: 'Are you sure you want to deactivate this user?',
            submitButtonTitle: 'Confirm',
            header: 'Deactivate user',
            onCancel: () => dispatch(ModalActions.hideModal()),
            onSubmit: () => {
                dispatch(UserManagementActions.deactivateUser())
                    .then(() => {
                        dispatch(ModalActions.hideModal());
                        window.router.transitionTo(`${paths.userManagementPage}/${paths.userManagementUsers}`);
                    })
                    .catch(() => {
                        // Todo: show error
                    });
            },
            useModalDialog: true,
        }));
    };

    let previewProperties;
    if (isPending || isInvitation) {
        if (isAdmin || allProperties) {
            previewProperties = currentUser.availableProperties;
        } else {
            previewProperties = currentUser.properties;
        }
    } else {
        previewProperties = currentUser.properties;
    }

    return (
        <div>
            {isInvitation && (
                <Row>
                    <Col>
                        <Text type="h4" emphasize={true}>
                            {`Please review ${currentUser.userName}'s access to the
                                properties and the features of this company account.`}
                        </Text>
                    </Col>
                </Row>
            )}
            <Row>
                <Col>
                    <div className="AddUserPage-preview-values">
                        <Text type="h4">
                            <span className="AddUserPage-bold">Email: </span>
                            {currentUser.email}
                            {currentUser.isPending && (
                                <React.Fragment>
                                    <Badge
                                        label="Pending"
                                        type="info"
                                    />
                                    <Link onClick={handleCancelInvitation}>
                                        Cancel Invite
                                    </Link>
                                </React.Fragment>
                            )}
                            {isYou && (
                                <Badge
                                    label="You"
                                    type="info"
                                />
                            )}
                        </Text>
                        <Text type="h4">
                            <span className="AddUserPage-bold">Name: </span>
                            {currentUser.userName}
                        </Text>
                        <Text type="h4">
                            <span className="AddUserPage-bold">Role: </span>
                            {isAdmin ? 'Company admin' : 'User'}
                            {!isYou && (
                                <Link onClick={onEditRole}>
                                    Edit
                                </Link>
                            )}
                        </Text>
                        <Text type="h4">
                            <span className="AddUserPage-bold">Access to: </span>
                            {allProperties ? 'All properties' : 'Limited Properties'}
                            {!isYou && !isAdmin && (
                                <Link onClick={onEditAllProperties}>
                                    Edit
                                </Link>
                            )}
                        </Text>
                    </div>
                </Col>
            </Row>
            <Row>
                <Col>
                    <div className="AddUserPage-list-box">
                        <div className="AddUserPage-list-box-header">
                            <Text type="h4">
                                {`${previewProperties.length} Properties`}
                            </Text>
                            {!isYou && !allProperties && (
                                <Link onClick={onEditProperties}>
                                    Edit
                                </Link>
                            )}
                            {allProperties && (
                                <Text>
                                    {`${currentUser.userName} will have access to any properties added in the future.`}
                                </Text>
                            )}
                        </div>
                        <div className="AddUserPage-list-box-body">
                            {previewProperties.map((property, i) => (
                                <div key={i} className="AddUserPage-list-box-row">
                                    <div className="AddUserPage-list-box-col">
                                        {property.name || property.street}
                                    </div>
                                    <div className="AddUserPage-list-box-col">
                                        {property.city}
                                    </div>
                                    <div className="AddUserPage-list-box-col">
                                        {property.state}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </Col>
            </Row>
            <Row>
                <Col>
                    <div className="AddUserPage-list-box">
                        <div className="AddUserPage-list-box-header">
                            <Text type="h4">
                                {`${currentUser.userName}'s Permissions`}
                            </Text>
                            {!isYou && !isAdmin && (
                                <Link onClick={onEditPermissions}>
                                    Edit
                                </Link>
                            )}
                        </div>
                        <div className="AddUserPage-list-box-body">
                            {Object.keys(otherPermissions).map(permission => (
                                <div key={permission} className="AddUserPage-list-box-item">
                                    {currentUser.permissions[permission] || isAdmin ?
                                        <Check size={16} className="permission-icon" />
                                        : <Close size={16} className="permission-icon" />}
                                    {UserPermissionsConstants.DESCRIPTION[permission].DESCRIPTION}
                                </div>
                            ))}
                        </div>
                    </div>
                </Col>
            </Row>
            {isInvitation && (
                <React.Fragment>
                    <HR />
                    <div className="Questions-footer">
                        <Link className="Questions-button" onClick={onCancel}>
                            Cancel
                        </Link>
                        {isInvitation && (
                            <div>
                                <Link className="Questions-button" onClick={onBack}>
                                    Back
                                </Link>
                                <Button type="primary" onClick={onSendInvite}>
                                    Send Invitation
                                </Button>
                            </div>
                        )}
                    </div>
                </React.Fragment>
            )}
            {!isYou && !isInvitation && !isPending && (
                <div className="AddUserPage-deactivate">
                    <Text>
                        {`Do you want to remove ${currentUser.userName} from the company account
                        and revoke their access to properties and features?`}
                    </Text>
                    <Button type="secondary" onClick={handleDeactivateAccount}>
                        Deactivate Account
                    </Button>
                </div>
            )}
        </div>
    );
};

Preview.propTypes = propTypes;

const mapStateToProps = state => ({
    currentUser: UserManagementSelectors.makeCurrentUser(state),
    user: UserSelectors.makeUser(state),
});

export default connect(mapStateToProps)(Preview);
